import { MetricPicker, PencilButton } from "@/components/MetricPicker";
import { Spark } from "@/components/Spark";
import { getMetric } from "@/lib/metrics";
import type { DailyPoint, OverviewKpis } from "@/lib/types";

export function ConfigurableKpi({
  metricId,
  onChange,
  exclude = [],
  kpis,
  comparison,
  series,
  readOnly = false,
}: {
  metricId: string;
  onChange?: (id: string) => void;
  exclude?: string[];
  kpis: OverviewKpis;
  comparison: OverviewKpis | null;
  series: DailyPoint[];
  // Pinned tiles (Spend + Installs on the inline editor) render with no
  // metric-swap pencil — the first two slots are fixed forever (§36).
  readOnly?: boolean;
}) {
  const m = getMetric(metricId);
  const current = m.fromKpis(kpis);
  const previous = comparison ? m.fromKpis(comparison) : null;
  // Pass raw values (null for days with no data) so Spark auto-
  // scales to the real min/max. Coercing null → 0 here was making
  // low-magnitude metrics like CPA $0.49 look like a flat line:
  // a single no-activity day at 0 was dominating the vertical range
  // and crowding the real values into a thin band at the top.
  const spark = series.map((p) => m.fromSeries(p));

  const d = deltaInfo(current, previous, m.goodDir);

  return (
    <div className={readOnly ? "kpi" : "kpi hover-reveal"}>
      {!readOnly && (
        <MetricPicker
          value={metricId}
          onChange={onChange ?? (() => {})}
          exclude={exclude}
          trigger={({ toggle, open }) => (
            <PencilButton active={open} onClick={toggle} />
          )}
        />
      )}
      <div className="kpi-label">{m.label}</div>
      <div className="kpi-value">
        {m.unit === "$" && <small>$</small>}
        <span>{m.fmt(current)}</span>
        {m.unit && m.unit !== "$" && <small>{m.unit}</small>}
      </div>
      <div className="kpi-foot">
        {d ? (
          <>
            <span className={`delta ${d.dir}`}>
              {/* Arrow = direction of the actual change (up if the
                  metric grew, down if it shrank). Color = whether
                  that change is good or bad for this metric —
                  declared via MetricDef.goodDir. Earlier the arrow
                  was keyed to good/bad which meant a CPA drop (a
                  win) rendered as ▲. */}
              {d.moved === "up" ? "▲" : "▼"} {d.text}
            </span>
            <span className="muted">vs prev period</span>
          </>
        ) : (
          // Back-end returns comparison = null when the previous
          // window has no base activity (spend/impressions/installs
          // all zero) or the metric itself has no prior value to
          // compare against. Either way: no meaningful delta.
          <span className="muted">No previous values</span>
        )}
      </div>
      <div className="kpi-spark">
        <Spark
          data={spark}
          color={
            d?.dir === "pos"
              ? "var(--c-pos-500)"
              : d?.dir === "neg"
              ? "var(--c-neg-500)"
              : m.color
          }
        />
      </div>
    </div>
  );
}

function deltaInfo(
  current: number | null,
  previous: number | null,
  goodDir: "up" | "down"
): { text: string; dir: "pos" | "neg"; moved: "up" | "down" } | null {
  if (current == null || previous == null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  // `moved` = direction the metric actually changed, drives the arrow.
  // `dir` = whether the change is good or bad for this metric, drives
  // the color. Decoupling the two is the whole point: a CPA drop
  // should render as ▼ (went down) in green (good), not ▲ in green.
  const moved: "up" | "down" = pct >= 0 ? "up" : "down";
  const dir: "pos" | "neg" = moved === goodDir ? "pos" : "neg";
  return {
    text: `${Math.abs(pct).toFixed(1)}%`,
    dir,
    moved,
  };
}
